/**
 * Makanly Design System v3.0 - Badge Component
 *
 * Variants:
 * - default: Teal (primary brand color)
 * - secondary: Gray muted
 * - success: Emerald green
 * - warning: Amber orange
 * - danger: Red
 * - info: Blue (use sparingly)
 * - outline: Border only
 * - accent: Gold for premium/rewards
 *
 * @see /docs/design/UI-DESIGN-CONTRACT.md
 */

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        // Default - Teal (brand color)
        default: 'border-transparent bg-primary-500 text-white hover:bg-primary-600',

        // Secondary - Gray muted
        secondary: 'border-transparent bg-gray-100 text-gray-700 hover:bg-gray-200',

        // Success - Emerald green
        success:
          'border-transparent bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',

        // Warning - Amber orange
        warning:
          'border-transparent bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',

        // Danger - Red
        danger: 'border-transparent bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',

        // Destructive - Alias for danger (backwards compatibility)
        destructive:
          'border-transparent bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',

        // Info - Blue (use sparingly, not for primary actions)
        info: 'border-transparent bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',

        // Outline - Border only
        outline: 'border-gray-200 text-foreground bg-transparent',

        // Accent - Gold for premium/rewards (NEW in v3.0)
        accent:
          'border-transparent bg-accent-100 text-accent-800 dark:bg-accent-900/30 dark:text-accent-400',

        // Premium - Gold with shimmer for loyalty/rewards (NEW in v3.0)
        premium:
          'border-transparent bg-gradient-to-r from-accent-400 via-accent-500 to-accent-400 text-gray-900 font-bold',

        // Teal subtle - Light teal background (NEW in v3.0)
        'teal-subtle':
          'border-transparent bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400',
      },
      size: {
        default: 'px-2.5 py-0.5 text-xs',
        sm: 'px-2 py-0.5 text-[10px]',
        lg: 'px-3 py-1 text-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  /**
   * Optional dot indicator before the badge text
   */
  dot?: boolean;
  /**
   * Dot color (only used when dot is true)
   */
  dotColor?: 'success' | 'warning' | 'danger' | 'info' | 'primary';
}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, size, dot, dotColor, children, ...props }, ref) => {
    const dotColorClass = {
      success: 'bg-emerald-500',
      warning: 'bg-amber-500',
      danger: 'bg-red-500',
      info: 'bg-blue-500',
      primary: 'bg-primary-500',
    };

    return (
      <div ref={ref} className={cn(badgeVariants({ variant, size }), className)} {...props}>
        {dot && (
          <span
            className={cn(
              'mr-1.5 h-1.5 w-1.5 rounded-full',
              dotColor ? dotColorClass[dotColor] : 'bg-current',
            )}
          />
        )}
        {children}
      </div>
    );
  },
);
Badge.displayName = 'Badge';

export { Badge, badgeVariants };
